import React from 'react';
import { Burrito, Grid, GridItem } from '../../helpers';
import ImageWithCaption from '../../components/Image/ImageWithCaption';
import { H3, Link } from '../../primitives';
import Spacer from '../../helpers/Spacer';
import styled from '@emotion/styled';
import { colors, mixins, text } from '../../core/styles/theme/default';
import { rem } from 'polished';
import { ImageInterface } from '~/types';

const StyledBox = styled.article`
  background: white;
  padding: ${rem(32)};
  box-shadow: 0px ${rem(20)} ${rem(25)} rgba(0, 0, 0, 0.1),
    0 ${rem(10)} ${rem(10)} rgba(0, 0, 0, 0.04);

  ${mixins.MQ('medium')} {
    padding: ${rem(58)};
  }
`;

const StyledTitle = styled(H3)``;

const StyledLead = styled.div`
  ${text.p2}
  ${({ theme }) => theme?.stylesExtension?.teaserCampaign?.lead}
`;

const StyledBackground = styled.div`
  width: 100vw;
  top: 0;

  ${mixins.responsiveStyles({
    left: {
      zero: '-16px',
      medium: '-24px',
      large: '-48px',
      wide: `calc(-1 * ((100vw - 1088px ) / 2))`,
      ultra: `calc(-1 * ((100vw - 1260px ) / 2))`,
    },
    position: {
      zero: 'relative',
      large: 'absolute',
    },
    height: {
      zero: '96vw',
      small: '60.5vw',
      large: '100%',
    },
  })}

  figure,
  .gatsby-image-wrapper {
    height: 100%;
  }
`;

const StyledBurrito = styled(Burrito)`
  z-index: 1;

  ${mixins.responsiveStyles({
    'margin-top': {
      zero: -36,
      medium: -96,
      large: 80,
    },
    'margin-bottom': {
      zero: 0,
      large: 80,
    },
  })}
`;

const StyledLink = styled(Link)`
  color: ${colors.nightBlue500};

  .m-richtext & {
    text-decoration: none;
  }

  ${({ theme }) => theme?.stylesExtension?.teaserCampaign?.link}
`;

const TeaserCampaign = (teaserCampaignProps: TeaserCampaignProps) => {
  if (!teaserCampaignProps) return null;
  const { title, lead, backgroundImage, logo, link } = teaserCampaignProps;

  return (
    <Grid columns={{ zero: 'full', large: 12 }}>
      <GridItem
        size={{ zero: 'full', small: 12, ultra: 12 }}
        push={{ zero: 0, ultra: 0 }}
      >
        <StyledBackground>
          <ImageWithCaption data={backgroundImage} />
        </StyledBackground>
        <StyledBurrito>
          <Grid columns={{ zero: 'full', small: 12, large: 12 }}>
            <GridItem
              size={{ zero: 'full', small: 12, large: 5 }}
              push={{ zero: 0, large: 2 }}
            >
              <StyledBox>
                {logo && (
                  <>
                    <ImageWithCaption
                      data={{
                        image: logo,
                        imageFill: false,
                        objectFit: 'contain',
                      }}
                    />
                    <Spacer space="epsilon" />
                  </>
                )}
                <StyledTitle>{title}</StyledTitle>
                <Spacer space="gamma" />
                <StyledLead>{lead}</StyledLead>
                <Spacer space="epsilon" />
                <StyledLink
                  variant={link.variant}
                  linkText={link.linkText}
                  link={{
                    href: link.url,
                    title: link.linkText,
                    target: link.variant === 'external' ? '_blank' : '_self',
                  }}
                />
              </StyledBox>
            </GridItem>
          </Grid>
        </StyledBurrito>
      </GridItem>
    </Grid>
  );
};

export type TeaserCampaignProps = {
  title: string;
  lead: string;
  backgroundImage: ImageInterface;
  logo: ImageInterface['image'];
  link: {
    linkText: string;
    url: string;
    variant: 'internal' | 'external';
  };
} | null;

export default TeaserCampaign;
